// =====================================================================
// RH — Dimensionamento de Escala: a aritmética da grade, no front
// =====================================================================
// ⚠️⚠️ ISTO É ESPELHO de server/rh/escala-motor.js, e existe por um motivo só:
//    o editor precisa redesenhar a curva a CADA CLIQUE. Ir ao servidor a cada
//    clique deixaria a grade lenta e inutilizável.
//
//    A AUTORIDADE CONTINUA SENDO O BACKEND: o PUT recalcula tudo e devolve o
//    resultado dele, que sobrescreve o que a tela mostrava. Este arquivo é
//    previsão; o servidor é o fato.
//
// ⚠️ Divergir do backend NÃO DÁ ERRO EM LUGAR NENHUM — a tela mostra um número,
//    o banco guarda outro, e ninguém percebe até alguém conferir na mão. Por
//    isso há teste (escala-grade-utils.test.js) travando as duas pontas com os
//    mesmos casos medidos que o teste do backend usa.

// ⚠️ A paleta vem de um arquivo CommonJS de propósito — ele é compartilhado com
//    o backend (o PDF). Ver o cabeçalho dele.
import {
    PALETA_CARGO, hueDoCargo, tomDoCargo, corDoBreak, corDaCelula,
    TEXTO_CELULA, textoDaCelula, corDoTotal, textoDoTotal,
    CORES_SEMAFORO, SEMAFORO_NEUTRO, corSemaforo,
} from './escala-paleta';

// Reexportados para a tela continuar importando tudo de um lugar só.
export {
    PALETA_CARGO, hueDoCargo, tomDoCargo, corDoBreak, corDaCelula,
    TEXTO_CELULA, textoDaCelula, corDoTotal, textoDoTotal,
    CORES_SEMAFORO, SEMAFORO_NEUTRO, corSemaforo,
};

// A grade começa às 7h e vira a meia-noite. Igual a HORAS do escala-motor.js.
export const HORAS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6];

export const PERIODOS = ['SEG-QUI', 'SEX-DOM'];

// ⚠️ ESPELHO de FATOR_EFETIVO. No 12x36 a pessoa trabalha dia sim, dia não —
// a mesma posição exige turma A e turma B.
export const FATOR_EFETIVO = { '12X36': 2, '5X2': 1 };

// Onde o break costuma cair, em horas já trabalhadas do turno (medido nas 462
// linhas da planilha: sempre entre a 3ª e a 7ª).
export const BREAK_MIN_HORAS_TURNO = 3;
export const BREAK_MAX_HORAS_TURNO = 7;

// O TIME do MySQL chega como 'HH:MM:SS'; a tela trabalha com número.
export function horaNum(valor) {
    if (typeof valor === 'number') return valor;
    const m = /^(\d{1,2}):/.exec(String(valor || ''));
    return m ? Number(m[1]) % 24 : NaN;
}

// As horas que o turno ocupa, virando a meia-noite. Intervalo MEIO-ABERTO:
// quem sai às 20h trabalhou a coluna das 19h e não a das 20h.
export function horasDoTurno(turno) {
    const ini = horaNum(turno.HORA_ENTRADA);
    const fim = horaNum(turno.HORA_SAIDA);
    const out = [];
    let h = ini;
    // Guarda contra turno malformado (entrada = saída) virar laço infinito na tela.
    for (let i = 0; i < 24 && h !== fim; i++) { out.push(h); h = (h + 1) % 24; }
    return out;
}

export const horasPagasDoTurno = (turno) => horasDoTurno(turno).length - 1;

export const rotuloTurno = (t) => {
    const p = (h) => String(horaNum(h)).padStart(2, '0');
    return `${t.ESCALA} ${p(t.HORA_ENTRADA)}-${p(t.HORA_SAIDA)}`;
};

// A curva de um grupo: quantas pessoas estão no chão em cada hora.
// grupo: { id_turno, qtd, breaks: { 15: 3, 16: 3 } }
export function curvaDoGrupo(grupo, porIdTurno) {
    const turno = porIdTurno.get(Number(grupo.id_turno));
    const linha = new Array(24).fill(null);   // null = fora do turno (célula vazia)
    if (!turno) return linha;
    for (const h of horasDoTurno(turno)) {
        const emBreak = Number(grupo.breaks?.[h] || 0);
        linha[HORAS.indexOf(h)] = grupo.qtd - emBreak;
    }
    return linha;
}

// A curva somada do período — é o que o gráfico desenha.
export function curvaDoPeriodo(grupos, porIdTurno) {
    const total = new Array(24).fill(0);
    for (const g of grupos) {
        const l = curvaDoGrupo(g, porIdTurno);
        for (let i = 0; i < 24; i++) total[i] += l[i] || 0;
    }
    return total;
}

export const horasDoPeriodo = (grupos, porIdTurno) =>
    curvaDoPeriodo(grupos, porIdTurno).reduce((a, b) => a + b, 0);

// ⚠️ EFETIVO = posições 12x36 × 2 + posições 5x2 do bloco SEX-DOM.
// O 5x2 é lido da semana cheia: no SEG-QUI ele aparece reduzido, que é a folga
// rodando, e ler dali subdimensionaria a loja.
export function efetivoDaVersao(gruposPorPeriodo, porIdTurno) {
    let p12 = 0, p5 = 0;
    for (const g of gruposPorPeriodo['SEX-DOM'] || []) {
        const turno = porIdTurno.get(Number(g.id_turno));
        if (!turno) continue;
        if (turno.ESCALA === '12X36') p12 += g.qtd; else p5 += g.qtd;
    }
    return {
        posicoes12x36: p12,
        posicoes5x2: p5,
        posicoes: p12 + p5,
        efetivo: p12 * FATOR_EFETIVO['12X36'] + p5 * FATOR_EFETIVO['5X2'],
    };
}

// Distribui `qtd` pessoas nos horários de break usuais do turno, o mais
// uniformemente possível. É o que o botão "distribuir" faz e o estado inicial
// de um grupo novo — ninguém deveria precisar clicar 8 vezes para começar.
export function distribuirBreaks(qtd, turno) {
    const horas = horasDoTurno(turno);
    const janela = horas.slice(BREAK_MIN_HORAS_TURNO, Math.min(BREAK_MAX_HORAS_TURNO + 1, horas.length - 1));
    const alvo = janela.length ? janela : horas.slice(1, -1);
    const breaks = {};
    if (!alvo.length || qtd <= 0) return breaks;
    for (let i = 0; i < qtd; i++) {
        const h = alvo[i % alvo.length];
        breaks[h] = (breaks[h] || 0) + 1;
    }
    return breaks;
}

// Reajusta a repartição de breaks quando a quantidade do grupo muda, mexendo o
// mínimo possível: tira dos horários mais cheios, devolve aos mais vazios.
export function ajustarBreaks(breaks, qtdAntiga, qtdNova, turno) {
    if (qtdNova <= 0) return {};
    if (qtdAntiga === 0) return distribuirBreaks(qtdNova, turno);
    const b = { ...breaks };
    let saldo = qtdNova - qtdAntiga;
    const horas = horasDoTurno(turno);
    while (saldo > 0) {
        // acrescenta no horário de break menos usado dentro da janela usual
        const candidatos = horas.slice(BREAK_MIN_HORAS_TURNO, Math.min(BREAK_MAX_HORAS_TURNO + 1, horas.length - 1));
        const alvo = (candidatos.length ? candidatos : horas.slice(1, -1))
            .reduce((m, h) => ((b[h] || 0) < (b[m] || 0) ? h : m));
        b[alvo] = (b[alvo] || 0) + 1;
        saldo--;
    }
    while (saldo < 0) {
        const comGente = Object.keys(b).filter((h) => b[h] > 0);
        if (!comGente.length) break;
        const alvo = comGente.reduce((m, h) => (b[h] > b[m] ? h : m));
        b[alvo] -= 1;
        if (b[alvo] === 0) delete b[alvo];
        saldo++;
    }
    return b;
}

// ---------------------------------------------------------------------
// ⚠️⚠️ A ARITMÉTICA DO BREAK — a trava que faltava
// ---------------------------------------------------------------------
// A regra é fechada, não é uma faixa: **cada pessoa tem exatamente UM break de
// 1h**. Logo `soma dos breaks === QTD`, sempre. Num turno de 12h com 3 pessoas
// são 3 breaks e 33 horas trabalhadas — não 32, não 34.
//
// ⚠️ O editor travava isso POR HORA (`breaks[h] < qtd`) e não no TOTAL, então
//    com 3 pessoas dava para pôr 3 breaks às 15h, 3 às 16h e 3 às 17h — nove
//    breaks para três pessoas, zerando a linha inteira sem aviso nenhum.
//    (Achado pelo Rodrigo em 15/09: "eu consigo ir clicando até zerar tudo".)
//
// ⚠️ A consequência de não travar não era só visual: `semBreak` ficava NEGATIVO
//    e a trava de salvar (`pendentes > 0`) deixava passar, porque um número
//    negativo não é maior que zero. A escala ia para o banco com gente
//    almoçando duas vezes.
export const somaBreaks = (grupo) =>
    Object.values(grupo?.breaks || {}).reduce((a, b) => a + Number(b || 0), 0);

// Quantas pessoas do grupo ainda não têm break marcado. Tem de ser ZERO para
// salvar: todo mundo almoça.
export const semBreak = (grupo) => Number(grupo?.qtd || 0) - somaBreaks(grupo);

// Os grupos que não fecham a conta — é o que o aviso nomeia, o que a grade
// pinta de vermelho e o que o salvamento recusa. ⚠️ Reporta os DOIS lados, e
// desde 15/09 a sobra é alcançável de propósito: o clique é livre e deixa pôr
// mais breaks do que gente, para que mover um horário não exija devolver antes.
// A função existe para que esse estado seja VISTO — é dela que saem o aviso e a
// linha vermelha — e para que ele não chegue ao banco.
export function gruposIrregulares(grupos) {
    return (grupos || [])
        .map((g) => ({ grupo: g, faltam: semBreak(g) }))
        .filter((x) => x.faltam !== 0);
}

// =====================================================================
// PALETA E ORDENAÇÃO DA GRADE
// =====================================================================
// Pedido do Rodrigo (14/09): "definir CORES para cada cargo e meio que um
// degradê para ir variando entre eles, então se temos sei lá 6 atendentes você
// coloca uma cor principal e vai variando ela pra sabermos que se trata deles
// mesmos — e a chave primária será CARGO × ESCALA, ou seja Atendente 5x2
// diferente de Atendente 12x36."
//
// Como isso vira regra:
//   · o MATIZ (hue) é do CARGO — bate o olho e sabe de quem se trata;
//   · a VARIAÇÃO (luminosidade) separa cada linha dentro do mesmo cargo, então
//     Atendente 12x36 e Atendente 5x2 são parentes visíveis, não cores alheias;
//   · a chave da cor é `CARGO|ESCALA|TURNO`, estável — a cor de uma linha não
//     pula quando outra é adicionada ou removida.

// ⚠️⚠️ A TABELA DE CORES MUDOU DE CASA (15/09): ela está em `escala-paleta.js`,
//    que é o único arquivo do módulo lido pela TELA e pelo BACKEND ao mesmo
//    tempo. O PDF da escala precisa sair com as MESMAS cores da grade, e duas
//    cópias de uma tabela de tons divergem no primeiro ajuste que alguém fizer
//    de um lado só — divergência que ninguém vê até imprimir. Ler o cabeçalho
//    de lá antes de mexer em qualquer tom: os valores são calculados por
//    contraste WCAG, não escolhidos a olho.
//    O resto da grade (o que é cor de cargo, o que é cor de break, por que o
//    TOTAL usa outra escala) continua explicado lá.

// ⚠️ A ORDEM vem da dtbRhEscalaCargo.ORDEM — é DADO, não código, e o Rodrigo a
// definiu como Atendente → ASG → Estoquista → Anfitrião. Cargo sem ordem
// cadastrada vai para o fim (999) em vez de para o começo por acidente.
export function ordenarGrupos(grupos, ordemPorCargo, porIdTurno) {
    const ordem = (c) => (ordemPorCargo?.[c] ?? 999);
    return [...grupos].sort((a, b) => {
        const d = ordem(a.cargo) - ordem(b.cargo);
        if (d !== 0) return d;
        if (a.cargo !== b.cargo) return a.cargo.localeCompare(b.cargo, 'pt-BR');
        const ta = porIdTurno.get(Number(a.id_turno));
        const tb = porIdTurno.get(Number(b.id_turno));
        if (!ta || !tb) return 0;
        // 12x36 antes de 5x2 (é a espinha da escala), depois por hora de entrada.
        if (ta.ESCALA !== tb.ESCALA) return ta.ESCALA === '12X36' ? -1 : 1;
        return horaNum(ta.HORA_ENTRADA) - horaNum(tb.HORA_ENTRADA);
    });
}

// Monta a paleta das linhas. ⚠️ Não depende mais da POSIÇÃO da linha: a cor sai
// de `cargo × escala`, então acrescentar ou remover uma linha não recolore as
// outras (o gradiente antigo fazia isso, e a grade "piscava" a cada edição).
export function paletaDosGrupos(grupos, porIdTurno) {
    const cores = new Map();
    for (const g of grupos) {
        const turno = porIdTurno.get(Number(g.id_turno));
        cores.set(chaveCor(g), tomDoCargo(g.cargo, turno ? turno.ESCALA : '12X36'));
    }
    return cores;
}

export const chaveCor = (g) => `${g.cargo}|${g.id_turno}`;


// =====================================================================
// ⚠️ TURNO NOTURNO — espelho de escala-motor.js
// =====================================================================
// A mesma escala rende horas diferentes quando o turno é noturno: a jornada é
// reduzida em ~1h. A classificação é DERIVADA das horas na janela 22h–05h, e
// não uma coluna que alguém marca — assim turno novo já nasce classificado.
//
// Medido em 2026-09-15 nos 21 turnos do catálogo: os 6 com jornada reduzida
// são exatamente os que têm 6 ou mais horas na janela; os demais têm no máximo 2.
export const JANELA_NOTURNA = [22, 23, 0, 1, 2, 3, 4];
export const HORAS_NOTURNAS_PARA_REDUZIR = 6;

export const horasNoturnasDoTurno = (turno) =>
    horasDoTurno(turno).filter((h) => JANELA_NOTURNA.includes(h)).length;

export const ehNoturno = (turno) => horasNoturnasDoTurno(turno) >= HORAS_NOTURNAS_PARA_REDUZIR;

// =====================================================================
// ⚠️ O RESUMO DO EFETIVO — a tabelinha que a planilha tinha no canto
// =====================================================================
// Rodrigo, 15/09: "queria que fosse parecido com a tabelinha que nós tínhamos
// no Excel, bem resumido, apontando quantos funcionários de cada cargo e cada
// escala precisaríamos para compor esse efetivo — e isso no total, considerando
// as regras que já conhecemos, como dobrar o 12x36."
//
// ⚠️ Lê o bloco SEX-DOM, que é a SEMANA CHEIA. O SEG-QUI mostra o 5x2 reduzido
//    (é a folga rodando) e usá-lo aqui subdimensionaria a loja.
// ⚠️ O fator sai de FATOR_EFETIVO, o mesmo do resto do módulo — não é uma
//    segunda conta que possa divergir da primeira.
export function resumoEfetivo(gruposPorPeriodo, porIdTurno, ordemPorCargo) {
    const porChave = new Map();
    for (const g of gruposPorPeriodo['SEX-DOM'] || []) {
        const turno = porIdTurno.get(Number(g.id_turno));
        if (!turno) continue;
        const escala = turno.ESCALA;
        const chave = `${g.cargo}|${escala}`;
        if (!porChave.has(chave)) {
            porChave.set(chave, { cargo: g.cargo, escala, posicoes: 0, horasDia: 0, fator: FATOR_EFETIVO[escala] || 1 });
        }
        porChave.get(chave).posicoes += g.qtd;
        // ⚠️ HORAS/DIA entregues pela escala = POSIÇÕES × (jornada − break), nunca o
        //    efetivo. Num dia só UMA turma trabalha: 3 pessoas de 12x36 entregam
        //    3×11h = 33h, não 66. (A tela multiplica por 30 para a estimativa de mês;
        //    no 12x36 esse ×30 já é o mês real — 2 turmas × 15 dias = 1 turma × 30.)
        porChave.get(chave).horasDia += g.qtd * horasPagasDoTurno(turno);
    }

    const ordem = (c) => (ordemPorCargo?.[c] ?? 999);
    const linhas = [...porChave.values()]
        .map((x) => ({ ...x, efetivo: x.posicoes * x.fator }))
        .sort((a, b) =>
            ordem(a.cargo) - ordem(b.cargo) ||
            a.cargo.localeCompare(b.cargo, 'pt-BR') ||
            // 12x36 antes do 5x2 — é a espinha da escala.
            (a.escala === b.escala ? 0 : a.escala === '12X36' ? -1 : 1));

    return {
        linhas,
        totalPosicoes: linhas.reduce((s, x) => s + x.posicoes, 0),
        totalEfetivo: linhas.reduce((s, x) => s + x.efetivo, 0),
        totalHorasDia: linhas.reduce((s, x) => s + x.horasDia, 0),
    };
}

// =====================================================================
// ⚠️ A PROPORÇÃO 12x36 × 5x2 (DESENHADA) — espelho de escala-motor.js
// =====================================================================
// O chip da tela recalcula a proporção DESENHADA a cada clique, sobre os grupos
// que já estão em memória. A REAL (do dtbConfere) NÃO se calcula aqui — vem
// pronta do servidor (GET da versão), porque depende do banco.
//
// ⚠️ ESPELHO: a régua abaixo é a mesma de escala-motor.js. Divergir não dá erro
//    — a tela pinta um semáforo e o PDF pinta outro. Há teste travando as duas
//    pontas (escala-grade-utils.test.js × escala-motor.test.js). Ver o cabeçalho
//    do motor para a régua inteira e o porquê do fallback em vermelho.
export const REGUA_PROPORCAO = {
    META: 70,
    VERDE_MIN: 65,
    VERDE_MAX: 75,
    AMARELO_ALTO_MAX: 79,
    AMARELO_BAIXO_MIN: 61,
};

// % de 12x36 → 'verde' | 'amarelo' | 'vermelho'. Sem base → null.
// ⚠️ Classifica no valor CRU (sem arredondar) — ver o motor.
export function classificarProporcao(pct) {
    if (pct === null || pct === undefined || Number.isNaN(Number(pct))) return null;
    const p = Number(pct);
    const r = REGUA_PROPORCAO;
    if (p >= r.VERDE_MIN && p <= r.VERDE_MAX) return 'verde';
    if ((p > r.VERDE_MAX && p <= r.AMARELO_ALTO_MAX) ||
        (p >= r.AMARELO_BAIXO_MIN && p < r.VERDE_MIN)) return 'amarelo';
    return 'vermelho';   // p > 79 ou p < 61 — o fallback que fecha o vão (60,61)
}

// A DESENHADA, sobre o EFETIVO do SEX-DOM (pessoas — 12x36 em dobro), para ser
// comparável com a Real (headcount). Espelho do motor. Base vazia → pct null.
export function proporcaoDesenhada(gruposPorPeriodo, porIdTurno) {
    const { posicoes12x36, posicoes5x2 } = efetivoDaVersao(gruposPorPeriodo, porIdTurno);
    const ef12 = posicoes12x36 * FATOR_EFETIVO['12X36'];   // ×2
    const ef5 = posicoes5x2 * FATOR_EFETIVO['5X2'];        // ×1
    const total = ef12 + ef5;
    const pct = total > 0 ? (100 * ef12) / total : null;
    return { pct, faixa: classificarProporcao(pct), n12: ef12, n5: ef5, total, posicoes12x36, posicoes5x2 };
}

// A REAL, filtrada aos cargos DESENHADOS — espelho de escala-motor.js. A matriz
// (crew ativo por cargo×família) vem pronta do servidor; aqui só se somam os
// cargos que estão no desenho AGORA (por isso recalcula a cada clique: adicionar
// um cargo ao desenho traz os reais daquele cargo para a conta, ao vivo).
// `cargos` é o conjunto de CARGO_ESCALA presentes no desenho.
export function proporcaoRealFiltrada(matriz, cargos) {
    const set = cargos instanceof Set ? cargos : new Set(cargos || []);
    let n12 = 0, n5 = 0, outros = 0, total = 0;
    for (const cargo of Object.keys(matriz || {})) {
        if (!set.has(cargo)) continue;
        const m = matriz[cargo];
        n12 += m['12x36']; n5 += m['5x2']; outros += m.outros; total += m.total;
    }
    const pct = total > 0 ? (100 * n12) / total : null;
    return { pct, faixa: classificarProporcao(pct), n12, n5, outros, total };
}
